import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/20/solid'
import { cpqRulesApi, type CpqRule, type CpqRuleInput, type CpqRuleCondition, type CpqApprovalConfig } from '../../lib/api'
import { Pagination } from '../../components/ui/Pagination'

const RULE_TYPES = [
  { value: 'max_discount', label: 'Maximum Discount' },
  { value: 'min_margin', label: 'Minimum Margin' },
  { value: 'max_value', label: 'Maximum Value' },
  { value: 'min_value', label: 'Minimum Value' },
  { value: 'blocked_product', label: 'Blocked Product' },
  { value: 'custom', label: 'Custom Rule' },
] as const

const CONDITION_FIELDS = [
  { value: 'discount_percent', label: 'Discount %' },
  { value: 'margin_percent', label: 'Margin %' },
  { value: 'line_total', label: 'Line Total' },
  { value: 'doc_total', label: 'Document Total' },
  { value: 'quantity', label: 'Quantity' },
] as const

const OPERATORS = [
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
] as const

const ACTIONS = [
  { value: 'warn', label: 'Warning', icon: ExclamationTriangleIcon, color: 'text-amber-600' },
  { value: 'require_approval', label: 'Require Approval', icon: ShieldCheckIcon, color: 'text-yellow-600' },
  { value: 'block', label: 'Block', icon: NoSymbolIcon, color: 'text-red-600' },
] as const

const USER_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sales_rep', label: 'Sales Rep' },
  { value: 'marketing', label: 'Marketing' },
]

const ACTION_COLORS: Record<string, { bg: string; text: string }> = {
  warn: { bg: 'bg-amber-100', text: 'text-amber-800' },
  require_approval: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  block: { bg: 'bg-red-100', text: 'text-red-800' },
}

const defaultRuleInput: CpqRuleInput = {
  name: '',
  description: '',
  ruleType: 'max_discount',
  condition: { field: 'discount_percent', operator: '>', value: 0, unit: '%' },
  action: 'warn',
  priority: 100,
  isActive: true,
}

export function CpqRulesPage() {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [showModal, setShowModal] = useState(false)
  const [editingRule, setEditingRule] = useState<CpqRule | null>(null)
  const [ruleInput, setRuleInput] = useState<CpqRuleInput>(defaultRuleInput)
  const queryClient = useQueryClient()

  const { data: response, isLoading } = useQuery({
    queryKey: ['cpq-rules', { page, limit }],
    queryFn: () => cpqRulesApi.list({ page, limit }),
  })

  const createMutation = useMutation({
    mutationFn: (data: CpqRuleInput) => cpqRulesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-rules'] })
      setShowModal(false)
      setRuleInput(defaultRuleInput)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CpqRuleInput> }) => cpqRulesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-rules'] })
      setShowModal(false)
      setEditingRule(null)
      setRuleInput(defaultRuleInput)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => cpqRulesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-rules'] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => cpqRulesApi.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-rules'] })
    },
  })

  const rules = response?.data || []

  const openCreateModal = () => {
    setEditingRule(null)
    setRuleInput(defaultRuleInput)
    setShowModal(true)
  }

  const openEditModal = (rule: CpqRule) => {
    setEditingRule(rule)
    setRuleInput({
      name: rule.name,
      description: rule.description || '',
      ruleType: rule.ruleType,
      condition: rule.condition,
      action: rule.action,
      approvalConfig: rule.approvalConfig || undefined,
      appliesToUserRoles: rule.appliesToUserRoles,
      priority: rule.priority,
      isActive: rule.isActive,
    })
    setShowModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: ruleInput })
    } else {
      createMutation.mutate(ruleInput)
    }
  }

  const handleDelete = (rule: CpqRule) => {
    if (confirm(`Are you sure you want to delete "${rule.name}"?`)) {
      deleteMutation.mutate(rule.id)
    }
  }

  const handleToggle = (rule: CpqRule) => {
    toggleMutation.mutate(rule.id)
  }

  const updateCondition = (field: keyof CpqRuleCondition, value: any) => {
    setRuleInput(prev => ({
      ...prev,
      condition: { ...prev.condition, [field]: value },
    }))
  }

  const formatCondition = (condition: CpqRuleCondition) => {
    const fieldLabel = CONDITION_FIELDS.find(f => f.value === condition.field)?.label || condition.field
    const unit = condition.unit === '%' ? '%' : condition.unit === 'EUR' ? '€' : ''
    return `${fieldLabel} ${condition.operator} ${unit}${condition.value}${unit === '%' ? '' : unit}`
  }

  if (isLoading) return <div className="p-8">Loading...</div>

  return (
    <>
      {/* Secondary column */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">CPQ Rules</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage pricing rules and guardrails
            </p>
          </div>

          <button
            onClick={openCreateModal}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            New Rule
          </button>

          {/* Rule Types Legend */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Action Types</h3>
            <dl className="space-y-2">
              {ACTIONS.map(action => (
                <div key={action.value} className="flex items-center gap-2 text-sm">
                  <action.icon className={`h-4 w-4 ${action.color}`} />
                  <dt className="text-gray-600">{action.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Summary</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Total Rules</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Active</dt>
                <dd className="font-medium text-green-600">{rules.filter(r => r.isActive).length}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Inactive</dt>
                <dd className="font-medium text-gray-400">{rules.filter(r => !r.isActive).length}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">CPQ Rules</h1>
            <button
              onClick={openCreateModal}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              New Rule
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Priority</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Rule</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Condition</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Action</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rules.map((rule) => (
                  <tr key={rule.id} className={`hover:bg-gray-50 ${!rule.isActive ? 'opacity-50' : ''}`}>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {rule.priority}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="font-medium text-gray-900">{rule.name}</div>
                      {rule.description && (
                        <div className="text-gray-500 text-xs max-w-xs truncate">{rule.description}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatCondition(rule.condition)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${ACTION_COLORS[rule.action]?.bg || 'bg-gray-100'} ${ACTION_COLORS[rule.action]?.text || 'text-gray-700'}`}>
                        {rule.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <button
                        onClick={() => handleToggle(rule)}
                        disabled={toggleMutation.isPending}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                          rule.isActive
                            ? 'text-green-700 bg-green-100 hover:bg-green-200'
                            : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {rule.isActive ? (
                          <>
                            <PlayIcon className="h-3 w-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <PauseIcon className="h-3 w-3" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(rule)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          disabled={deleteMutation.isPending}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                      No rules found. Create your first rule to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  {editingRule ? 'Edit Rule' : 'Create Rule'}
                </h3>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      required
                      value={ruleInput.name}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      rows={2}
                      value={ruleInput.description}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, description: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    />
                  </div>

                  {/* Rule Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rule Type</label>
                    <select
                      value={ruleInput.ruleType}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, ruleType: e.target.value as any }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    >
                      {RULE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Condition */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={ruleInput.condition.field}
                        onChange={(e) => updateCondition('field', e.target.value)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                      >
                        {CONDITION_FIELDS.map(field => (
                          <option key={field.value} value={field.value}>{field.label}</option>
                        ))}
                      </select>
                      <select
                        value={ruleInput.condition.operator}
                        onChange={(e) => updateCondition('operator', e.target.value)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                      >
                        {OPERATORS.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        required
                        value={ruleInput.condition.value}
                        onChange={(e) => updateCondition('value', parseFloat(e.target.value) || 0)}
                        className="block rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                      />
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-500">Unit</label>
                      <select
                        value={ruleInput.condition.unit || ''}
                        onChange={(e) => updateCondition('unit', e.target.value || undefined)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                      >
                        <option value="">None</option>
                        <option value="%">Percentage (%)</option>
                        <option value="EUR">Euro (EUR)</option>
                        <option value="qty">Quantity</option>
                      </select>
                    </div>
                  </div>

                  {/* Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Action</label>
                    <select
                      value={ruleInput.action}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, action: e.target.value as any }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    >
                      {ACTIONS.map(action => (
                        <option key={action.value} value={action.value}>{action.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Applies to Roles */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Applies to Roles (leave empty for all)</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {USER_ROLES.map(role => (
                        <label key={role.value} className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={ruleInput.appliesToUserRoles?.includes(role.value) || false}
                            onChange={(e) => {
                              const currentRoles = ruleInput.appliesToUserRoles || []
                              if (e.target.checked) {
                                setRuleInput(prev => ({
                                  ...prev,
                                  appliesToUserRoles: [...currentRoles, role.value],
                                }))
                              } else {
                                setRuleInput(prev => ({
                                  ...prev,
                                  appliesToUserRoles: currentRoles.filter(r => r !== role.value),
                                }))
                              }
                            }}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="ml-2 text-sm text-gray-600">{role.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority (lower = higher priority)</label>
                    <input
                      type="number"
                      value={ruleInput.priority}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    />
                  </div>

                  {/* Active */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={ruleInput.isActive}
                      onChange={(e) => setRuleInput(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Active</label>
                  </div>
                </div>

                <div className="mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="inline-flex w-full justify-center rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 sm:col-start-2 disabled:opacity-50"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editingRule ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
